import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getStageIndex } from './orderStageMap';
import { useParams } from 'react-router-dom';
import OrderConfirmation from './OrderConfirmation';
import OrderProcessing from './OrderProcessing';
import OrderDeliveryPickup from './OrderDeliveryPickup';
import OrderDelivered from './OrderDelivered';
import OrderFeedback from './OrderFeedback';
import OrderHistory from './OrderHistoryPage';
import OrderPending from './OrderPending';
import { 
  Container, 
  Stepper, 
  Step, 
  StepLabel, 
  Box, 
  Typography, 
  CircularProgress, 
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  Chip,
  alpha
} from '@mui/material';
import {
  Receipt as OrderIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import config from './config';
import { useTranslation } from 'react-i18next';

// API base URL
const API_BASE_URL = `${config.API_BASE_URL}/Orders`;

const OrderStatusPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const getStageName = (status) => {
    switch (status) {
      case 'Pending': return t('trackOrder.status.pending') || 'Pending';
      case 'Order Confirmed': return t('trackOrder.status.confirmed') || 'Confirmed';
      case 'Preparing': 
      case 'Processing':
      case 'processing':
      case 'proccessing':
        return t('trackOrder.status.preparing') || 'Preparing';
      case 'Out for Delivery': return t('trackOrder.status.outForDelivery') || 'Out for Delivery';
      case 'Ready for Pickup': return t('trackOrder.status.readyForPickup') || 'Ready for Pickup';
      case 'Delivered': return t('trackOrder.status.delivered') || 'Delivered';
      case 'Picked Up': return t('trackOrder.status.completed') || 'Completed';
      case 'Feedback': return t('pending.feedback.title') || 'Feedback';
      case 'Completed': return t('trackOrder.status.completed') || 'Completed';
      default: return t('orderStatus.unknownStage') || 'Unknown';
    }
  };

  const stepsLabels = [
    t('trackOrder.status.pending') || 'Pending',
    t('trackOrder.status.confirmed') || 'Confirmed',
    t('trackOrder.status.preparing') || 'Preparing',
    t('trackOrder.status.outForDelivery') || 'Delivery',
    t('trackOrder.status.completed') || 'Completed',
    t('pending.feedback.title') || 'Feedback'
  ];

  // Get current user from localStorage
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  })();
  
  const userId = user?.Id;
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug: log order status
  if (order) {
    console.log('OrderStatusPage: order.Status =', order.Status, 'order.OrderStatus =', order.OrderStatus);
  }

  // Robust polling
  useEffect(() => {
    if (!orderId || !userId) {
      setOrder(null);
      setLoading(false);
      setError(t('loginRequired') || 'Please login to view order status.');
      return;
    }

    let isMounted = true;
    let intervalId = null;

    const fetchOrder = async () => {
      if (!isMounted) return;
      try {
        console.log('[OrderStatusPage] Fetching order status...');
        const res = await axios.get(`${API_BASE_URL}/${orderId}?userId=${userId}`);
        if (isMounted) {
          setOrder(res.data);
          setStep(getStageIndex(res.data.Status));
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(t('orderFetchError') || 'Failed to fetch order status');
          setLoading(false);
        }
      }
    };

    fetchOrder();
    intervalId = setInterval(fetchOrder, 30000);
    console.log('[OrderStatusPage] Polling interval started:', intervalId);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
        console.log('[OrderStatusPage] Polling interval cleared:', intervalId);
      }
    };
  }, [orderId, userId, t]);

  const steps = order ? [
    <OrderPending order={order} stageName={getStageName('Pending')} />,
    <OrderConfirmation order={order} stageName={getStageName('Order Confirmed')} />,
    <OrderProcessing order={order} stageName={getStageName('Preparing')} />,
    <OrderDeliveryPickup order={order} stageName={getStageName('Out for Delivery')} />,
    <OrderDelivered order={order} stageName={getStageName('Delivered')} />,
    <OrderFeedback 
      order={order}
      onSubmitted={() => setStep(step+1)}
    />,
    <OrderHistory order={{ ...order, feedback }} stageName={getStageName('Completed')} />,
  ] : [];

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh',
        gap: 2
      }}>
        <CircularProgress size={isMobile ? 50 : 60} />
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
        >
          {t('loadingOrder') || 'Loading order...'}
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 8, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        <Alert 
          severity="error" 
          icon={<InfoIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
          sx={{ 
            borderRadius: { xs: 2, sm: 3 },
            fontSize: { xs: '0.9rem', sm: '1rem' },
            p: { xs: 2, sm: 2.5 }
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }
  
  if (!orderId) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 8, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="warning" sx={{ borderRadius: { xs: 2, sm: 3 } }}>
          {t('noOrderId') || 'No order ID provided.'}
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 8, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="info" sx={{ borderRadius: { xs: 2, sm: 3 } }}>
          {t('orderNotFound') || 'Order not found.'}
        </Alert>
      </Container>
    );
  }

  console.log('OrderStatusPage render:', { step, steps, current: steps[step] });
  
  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: { xs: 10, sm: 12 }, 
        mb: { xs: 6, sm: 8 },
        px: { xs: 2, sm: 3 }
      }}
    >
      {/* ✅ Order Info Header (Mobile-Optimized) */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: { xs: 3, sm: 4 },
          borderRadius: { xs: 2, sm: 3 },
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.2)
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <OrderIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography 
                variant="h6" 
                fontWeight={700}
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                {t('orderTracking') || 'Order Tracking'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              >
                {t('orderId') || 'Order ID'}: <strong>{orderId}</strong>
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={getStageName(order.Status)}
            color="primary"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              height: { xs: 28, sm: 32 },
              fontWeight: 600,
              alignSelf: { xs: 'flex-start', sm: 'center' }
            }}
          />
        </Stack>
      </Paper>

      {/* ✅ Mobile-Optimized Stepper */}
      <Box sx={{ width: '100%', mb: { xs: 4, sm: 6 } }}>
        <Stepper 
          activeStep={step} 
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{
            '& .MuiStepConnector-line': {
              borderColor: isMobile ? 'divider' : undefined
            }
          }}
        >
          {stepsLabels.map((label, index) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{
                  sx: { 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    '&.Mui-active': { 
                      color: theme.palette.primary.main,
                      transform: isMobile ? 'none' : 'scale(1.1)'
                    },
                    '&.Mui-completed': { 
                      color: theme.palette.success.main 
                    },
                  }
                }}
              >
                <Typography 
                  sx={{ 
                    fontFamily: 'Cairo, sans-serif', 
                    fontWeight: step === index ? 700 : 600,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    color: step === index ? 'primary.main' : 'text.secondary'
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      {/* ✅ Content Area with Mobile-Optimized Background */}
      <Box sx={{ 
        position: 'relative',
        minHeight: { xs: 300, sm: 400 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '110%', sm: '120%' },
          height: { xs: '110%', sm: '120%' },
          background: 'radial-gradient(circle, rgba(35,39,42,0.05) 0%, rgba(255,255,255,0) 70%)',
          zIndex: -1,
          borderRadius: '50%',
          pointerEvents: 'none'
        }
      }}>
        {steps[step]}
      </Box>
    </Container>
  );
};

export default OrderStatusPage;